import React from 'react'
import { useTranslation } from 'react-i18next'

import { Calendar } from 'primereact/calendar'
import { InputNumber } from 'primereact/inputnumber'
import { InputText } from 'primereact/inputtext'
import { InputTextarea } from 'primereact/inputtextarea'

import { PanelContainer } from '../../../../components/Panel'
import { getConfigTranslation } from '../../../../utils/helper/getConfigTranslation'
import { ConfigurableComponent } from '../../../../utils/wrapper'
import { useConfigContext } from '../../../ui-config/context'
import { useInvoiceById } from '../../data'

type Props = {
  invoiceId: string
}

export const OrderInformation: React.FC<Props> = ({ invoiceId }) => {
  const { i18n } = useTranslation()
  const { language } = i18n

  const { data: invoice, isLoading } = useInvoiceById(invoiceId)
  const { uiConfig: config } = useConfigContext()

  const currency = invoice?.Payload.OrderCurrency || 'EUR'

  // ************************************************************
  // components
  // ************************************************************
  // orderId
  const orderIdLabel = <label className="text-sm" htmlFor="orderId"></label>

  const orderIdComponent = (
    <InputText
      id="orderId"
      value={invoice?.Payload.OrderId}
      className="w-full"
      disabled
    />
  )

  const orderIdWrapper = (
    <ConfigurableComponent
      config={config.invoice.orderInformation}
      component={orderIdComponent}
      label={orderIdLabel}
      className="col-12 md:col-6 lg:col-4 xl:col-3"
      isLoading={isLoading}
    />
  )

  // orderDate
  const orderDateLabel = <label className="text-sm" htmlFor="orderDate"></label>

  const orderDateComponent = (
    <Calendar
      id="orderDate"
      value={new Date(invoice?.Payload.OrderDate!)}
      className="w-full"
      locale={language}
      disabled
    />
  )

  const orderDateWrapper = (
    <ConfigurableComponent
      config={config.invoice.orderInformation}
      component={orderDateComponent}
      label={orderDateLabel}
      className="col-12 md:col-6 lg:col-4 xl:col-3"
      isLoading={isLoading}
    />
  )

  // orderDeliveryDate
  const orderDeliveryDateLabel = (
    <label className="text-sm" htmlFor="orderDeliveryDate"></label>
  )

  const orderDeliveryDateComponent = (
    <Calendar
      id="orderDeliveryDate"
      value={new Date(invoice?.Payload.OrderDeliveryDate!)}
      className="w-full"
      locale={language}
      disabled
    />
  )

  const orderDeliveryDateWrapper = (
    <ConfigurableComponent
      config={config.invoice.orderInformation}
      component={orderDeliveryDateComponent}
      label={orderDeliveryDateLabel}
      className="col-12 md:col-6 lg:col-4 xl:col-3"
      isLoading={isLoading}
    />
  )

  // termsOfDelivery
  const termsOfDeliveryLabel = (
    <label className="text-sm" htmlFor="termsOfDelivery"></label>
  )

  const termsOfDeliveryComponent = (
    <div id="termsOfDelivery" className="p-inputgroup">
      <span className="p-inputgroup-addon">
        {invoice?.Payload.TermsOfDelivery?.Code}
      </span>
      <InputText
        value={invoice?.Payload.TermsOfDelivery?.Description}
        className="w-full"
        disabled
      />
    </div>
  )

  const termsOfDeliveryWrapper = (
    <ConfigurableComponent
      config={config.invoice.orderInformation}
      component={termsOfDeliveryComponent}
      label={termsOfDeliveryLabel}
      className="col-12 md:col-6 lg:col-4 xl:col-3"
      isLoading={isLoading}
    />
  )

  // orderCurrency
  const orderCurrencyLabel = (
    <label className="text-sm" htmlFor="orderCurrency"></label>
  )

  const orderCurrencyComponent = (
    <InputText
      id="orderCurrency"
      value={invoice?.Payload.OrderCurrency}
      className="w-full"
      disabled
    />
  )

  const orderCurrencyWrapper = (
    <ConfigurableComponent
      config={config.invoice.orderInformation}
      component={orderCurrencyComponent}
      label={orderCurrencyLabel}
      className="col-12 md:col-6 lg:col-4 xl:col-3"
      isLoading={isLoading}
    />
  )

  // confirmationTotalNetItemAmount
  const confirmationTotalNetItemAmountLabel = (
    <label className="text-sm" htmlFor="confirmationTotalNetItemAmount"></label>
  )

  const confirmationTotalNetItemAmountComponent = (
    <InputNumber
      id="confirmationTotalNetItemAmount"
      value={invoice?.Payload.ConfirmationTotalNetItemAmount}
      mode="currency"
      currency={currency}
      locale={language}
      className="w-full"
      disabled
    />
  )

  const confirmationTotalNetItemAmountWrapper = (
    <ConfigurableComponent
      config={config.invoice.orderInformation}
      component={confirmationTotalNetItemAmountComponent}
      label={confirmationTotalNetItemAmountLabel}
      className="col-12 md:col-6 lg:col-4 xl:col-3"
      isLoading={isLoading}
    />
  )

  // orderNotes
  const orderNotesLabel = (
    <label className="text-sm" htmlFor="orderNotes"></label>
  )

  const orderNotesComponent = (
    <InputTextarea
      id="orderNotes"
      value={invoice?.Payload.OrderNotes}
      rows={5}
      className="w-full"
      disabled
    />
  )

  const orderNotesWrapper = (
    <ConfigurableComponent
      config={config.invoice.orderInformation}
      component={orderNotesComponent}
      label={orderNotesLabel}
      className="col-12"
      isLoading={isLoading}
    />
  )

  return (
    <PanelContainer
      header={getConfigTranslation(config.invoice.orderInformation.title)}
      id="invoice-orderInformation"
      visible={config.invoice.orderInformation.enabled}
    >
      <div className="formgrid grid">
        {/* orderId */}
        {orderIdWrapper}

        {/* orderDate */}
        {orderDateWrapper}

        {/* orderDeliveryDate */}
        {orderDeliveryDateWrapper}

        {/* termsOfDelivery */}
        {termsOfDeliveryWrapper}

        {/* orderCurrency */}
        {orderCurrencyWrapper}

        {/* confirmationTotalNetItemAmount */}
        {confirmationTotalNetItemAmountWrapper}

        {/* orderNotes */}
        {orderNotesWrapper}
      </div>
    </PanelContainer>
  )
}
